import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Search } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import { CtaBand } from "../components/CtaBand";
import { Seo, breadcrumbJsonLd } from "../components/Seo";
import { T, paths } from "../i18n";
import { CITIES, REGIONS } from "../data/cities";

export default function CitiesIndex({ lang }) {
  const t = T[lang];
  const p = paths(lang);
  const [q, setQ] = useState("");
  const [region, setRegion] = useState("all");

  const filtered = useMemo(() => CITIES.filter((c) => {
    const name = (lang === "fr" ? c.fr : c.en).toLowerCase();
    const matchQ = name.includes(q.toLowerCase()) || c.blurbEn.toLowerCase().includes(q.toLowerCase());
    return matchQ && (region === "all" || c.region === region);
  }), [q, region, lang]);

  const title = lang === "fr" ? "Villes desservies au Québec — 60+ villes | 1st Nations Security" : "Quebec Cities We Serve — 60+ Locations | 1st Nations Security";
  const desc = lang === "fr"
    ? "1st Nations Security dessert plus de 60 villes du Québec : Montréal, Québec, Laval, Gatineau, Longueuil, Sherbrooke, Saguenay, Lévis, Trois-Rivières et plus. Gardiens licenciés BSP, répartition 24/7."
    : "1st Nations Security serves 60+ Quebec cities: Montreal, Quebec City, Laval, Gatineau, Longueuil, Sherbrooke, Saguenay, Lévis, Trois-Rivières and more. BSP-licensed guards, 24/7 dispatch.";

  return (
    <div data-testid="cities-index-page">
      <Seo lang={lang} title={title} description={desc} enPath="/quebec-locations" frPath="/fr/villes-quebec"
        jsonLd={breadcrumbJsonLd([[t.breadcrumbHome, p.home], [t.breadcrumbCities, p.cities]])} />
      <PageHeader lang={lang} eyebrow={t.brandTag} title={t.citiesTitle}
        breadcrumbs={[[t.breadcrumbHome, p.home], [t.breadcrumbCities, null]]} />

      <div className="mx-auto max-w-7xl px-4 pt-10 sm:px-6 lg:px-8">
        <div className="relative max-w-xl">
          <Search size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t.searchCity}
            className="w-full rounded-full border border-slate-700 bg-card/80 py-3 pl-11 pr-4 text-sm text-slate-200 placeholder:text-slate-500 focus:border-amber-500/60 focus:outline-none"
            data-testid="city-search-input" />
        </div>
        <div className="mt-5 flex flex-wrap gap-2" data-testid="region-filters">
          <button onClick={() => setRegion("all")} className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${region === "all" ? "bg-amber-500 text-obsidian" : "border border-slate-700 text-slate-300 hover:border-amber-500/50"}`} data-testid="region-filter-all">
            {t.allRegions}
          </button>
          {Object.entries(REGIONS).map(([key, r]) => (
            <button key={key} onClick={() => setRegion(key)} className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${region === key ? "bg-amber-500 text-obsidian" : "border border-slate-700 text-slate-300 hover:border-amber-500/50"}`} data-testid={`region-filter-${key}`}>
              {r[lang]}
            </button>
          ))}
        </div>
      </div>

      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((c, i) => (
            <Reveal key={c.slug} delay={(i % 3) * 0.06}>
              <Link to={p.city(c.slug)} className="group flex h-full flex-col rounded-2xl border border-slate-800/70 bg-card/60 p-6 transition-all hover:-translate-y-1 hover:border-amber-500/40" data-testid={`city-card-${c.slug}`}>
                <div className="flex items-center justify-between">
                  <MapPin size={20} className="text-amber-400" />
                  <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-slate-500">{REGIONS[c.region][lang]}</span>
                </div>
                <h2 className="mt-4 font-display text-xl font-bold text-slate-100 group-hover:text-amber-300">{lang === "fr" ? c.fr : c.en}</h2>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-400">{lang === "fr" ? c.blurbFr : c.blurbEn}</p>
                <span className="mt-4 flex items-center gap-1.5 text-xs font-semibold text-amber-400">{t.viewCity} <ArrowRight size={13} className="transition-transform group-hover:translate-x-1" /></span>
              </Link>
            </Reveal>
          ))}
        </div>
        {filtered.length === 0 && <p className="py-12 text-center text-slate-500" data-testid="no-cities-found">—</p>}
      </section>
      <CtaBand lang={lang} />
    </div>
  );
}
